import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Location } from "@/lib/locationContext";
import type { LocationStatus } from "./LocationCard";

interface CompareTabProps {
  currentLocation: Location;
  allLocations: Location[];
  allScores: Record<string, number>;
  allStatuses: Record<string, LocationStatus>;
}

export default function CompareTab({ currentLocation, allLocations, allScores, allStatuses }: CompareTabProps) {
  const otherLocations = allLocations.filter(l => l.id !== currentLocation.id);
  const [compareId, setCompareId] = useState<string>(otherLocations[0]?.id || "");
  const compareLoc = allLocations.find(l => l.id === compareId);

  if (otherLocations.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground">Add a second location to compare performance side by side.</p>
      </div>
    );
  }

  const statusLabel = (s: LocationStatus) => s === "healthy" ? "Healthy" : s === "at-risk" ? "At Risk" : "Pre-Opening";

  const rows = [
    { label: "City", a: currentLocation.city || "—", b: compareLoc?.city || "—" },
    { label: "Type", a: currentLocation.business_type || "—", b: compareLoc?.business_type || "—" },
    { label: "Status", a: statusLabel(allStatuses[currentLocation.id] || "pre-opening"), b: statusLabel(allStatuses[compareId] || "pre-opening") },
    { label: "Score", a: `${allScores[currentLocation.id] || 0}%`, b: `${allScores[compareId] || 0}%` },
    { label: "Revenue", a: "—", b: "—" },
    { label: "Labor %", a: "—", b: "—" },
    { label: "Food Cost %", a: "—", b: "—" },
    { label: "Rent %", a: "—", b: "—" },
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Compare with</label>
        <Select value={compareId} onValueChange={setCompareId}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {otherLocations.map(l => (
              <SelectItem key={l.id} value={l.id} className="text-xs">{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left p-2.5 font-medium text-muted-foreground">Metric</th>
              <th className="text-right p-2.5 font-medium text-foreground">{currentLocation.name}</th>
              <th className="text-right p-2.5 font-medium text-foreground">{compareLoc?.name || "—"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border" : ""}>
                <td className="p-2.5 text-muted-foreground">{row.label}</td>
                <td className="p-2.5 text-right font-medium text-foreground">{row.a}</td>
                <td className="p-2.5 text-right font-medium text-foreground">{row.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-muted-foreground">Revenue data will populate after Monthly Check-Ins are submitted for each location.</p>
    </div>
  );
}
